import { cva } from 'class-variance-authority';
import React from 'react';

import { cn } from '@/lib/utils';

import { STATUS_BADGE_APPEARANCE, STATUS_BADGE_SIZE, STATUS_TONE } from './types/status-badge-variants-enums';








const foundationStatusBadgeVariants = cva('inline-flex items-center', {
  variants: {
    tone: {
      [STATUS_TONE.NEUTRAL]: 'text-status-neutral-foreground',
      [STATUS_TONE.SUCCESS]: 'text-status-success-foreground',
      [STATUS_TONE.WARNING]: 'text-status-warning-foreground',
      [STATUS_TONE.DANGER]: 'text-status-danger-foreground',
      [STATUS_TONE.INFO]: 'text-status-info-foreground',
    },
    appearance: {
      [STATUS_BADGE_APPEARANCE.DOT]: 'font-normal',
      [STATUS_BADGE_APPEARANCE.PILL]: 'rounded-full border font-medium',
    },
    size: {
      [STATUS_BADGE_SIZE.COMPACT]: '',
      [STATUS_BADGE_SIZE.DEFAULT]: '',
    },
  },
  compoundVariants: [
    {
      appearance: STATUS_BADGE_APPEARANCE.DOT,
      size: STATUS_BADGE_SIZE.COMPACT,
      class: 'gap-1.5 type-micro',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.DOT,
      size: STATUS_BADGE_SIZE.DEFAULT,
      class: 'gap-1.5 type-field-label',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      size: STATUS_BADGE_SIZE.COMPACT,
      class: 'min-h-5 gap-1.5 px-1.5 type-settings-row-meta',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      size: STATUS_BADGE_SIZE.DEFAULT,
      class: 'min-h-6 gap-1.5 px-2 type-field-label',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      tone: STATUS_TONE.NEUTRAL,
      class:
        'border-status-neutral-border bg-status-neutral-surface text-status-neutral-foreground',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      tone: STATUS_TONE.SUCCESS,
      class:
        'border-status-success-border bg-status-success-surface text-status-success-foreground',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      tone: STATUS_TONE.WARNING,
      class:
        'border-status-warning-border bg-status-warning-surface text-status-warning-foreground',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      tone: STATUS_TONE.DANGER,
      class:
        'border-status-danger-border bg-status-danger-surface text-status-danger-foreground',
    },
    {
      appearance: STATUS_BADGE_APPEARANCE.PILL,
      tone: STATUS_TONE.INFO,
      class: 'border-status-info-border bg-status-info-surface text-status-info-foreground',
    },
  ],
  defaultVariants: {
    tone: STATUS_TONE.NEUTRAL,
    appearance: STATUS_BADGE_APPEARANCE.DOT,
    size: STATUS_BADGE_SIZE.COMPACT,
  },
});

const foundationStatusIndicatorVariants = cva('shrink-0 rounded-full', {
  variants: {
    tone: {
      [STATUS_TONE.NEUTRAL]: 'bg-status-neutral-solid',
      [STATUS_TONE.SUCCESS]: 'bg-status-success-solid',
      [STATUS_TONE.WARNING]: 'bg-status-warning-solid',
      [STATUS_TONE.DANGER]: 'bg-status-danger-solid',
      [STATUS_TONE.INFO]: 'bg-status-info-solid',
    },
    size: {
      [STATUS_BADGE_SIZE.COMPACT]: 'size-1.5',
      [STATUS_BADGE_SIZE.DEFAULT]: 'size-2',
    },
  },
  defaultVariants: {
    tone: STATUS_TONE.NEUTRAL,
    size: STATUS_BADGE_SIZE.COMPACT,
  },
});

export interface StatusBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: STATUS_TONE;
  label: string;
  appearance?: STATUS_BADGE_APPEARANCE;
  size?: STATUS_BADGE_SIZE;
}

export const StatusBadge = React.memo(function StatusBadge({
  tone,
  appearance = STATUS_BADGE_APPEARANCE.DOT,
  size = STATUS_BADGE_SIZE.COMPACT,
  label,
  className,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        foundationStatusBadgeVariants({
          tone,
          appearance,
          size,
        }),
        className
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={foundationStatusIndicatorVariants({ tone, size })}
      />
      {label}
    </span>
  );
});
